import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Stack,
  Group,
  Title,
  Text,
  Button,
  Box,
  AppShell,
  Burger,
  Anchor,
  SimpleGrid,
  Paper,
  Image,
  Avatar,
  Badge,
  Grid,
  ThemeIcon,
  SegmentedControl,
  ActionIcon
} from '@mantine/core';
import { useDisclosure, useWindowScroll } from '@mantine/hooks';

// Icons
import {
  IconSparkles, IconTools, IconSchool, IconDots, IconUserStar, IconDeviceLaptop,
  IconStethoscope, IconBriefcase, IconRocket, IconBuildingStore, IconTruckDelivery,
  IconUsers, IconCamera, IconHomeHand, IconChartBar, IconCooker, IconWallet,
  IconAward, IconQuote, IconBrandApple, IconBrandGooglePlay, IconDownload,
  IconBrandInstagram, IconBrandTelegram, IconGift, IconMessageStar,
  IconTrophy, IconArrowRight
} from '@tabler/icons-react';

// Animation & Assets
import { motion, useScroll, useTransform, type Variants } from 'framer-motion';
import { ReactComponent as RuyaLogo } from './ruya.svg';
import ruyaNameLogo from './ruya-name.png';

// --- TYPES & CONSTANTS ---

type Lang = 'ru' | 'uz' | 'en';

const TRANSLATIONS = {
  en: {
    nav: { about: 'About', how: 'How it works', adv: 'Advantages', help: 'Help', app: 'Download App' },
    hero: {
      title: <>Start your journey <br />with RU’YA</>,
      text: <>RU’YA is a modern platform created for those who want to act, develop, and earn. It brings together people with any skills and those who need help with tasks, services, or small projects.<br /><br />Here private users and businesses easily find performers, while youth and specialists find opportunities for self-realization and income.</>,
      btnApp: 'Download App',
      btnHow: 'How it works'
    },
    about: {
      title: 'We are RU’YA',
      mainText: <><Text component="span" fw={700} c="salmon.6" inherit>RU’YA</Text> is more than just a platform. It is an opportunity for everyone to <Text component="span" fw={700} inherit>find work and earn</Text>.</>,
      subText: <>Find tasks that match your <Text component="span" fw={600} c="grayTurquoise.9" inherit>unique skills</Text> and hobbies. It is a way for the community to inspire people with diverse talents.</>,
      cards: [
        { title: 'Safe Deal Guarantee', desc: <>The built-in <Text component="span" fw={700}>secure payment</Text> system protects finances until the work is fully completed.</> },
        { title: 'Freedom of Choice', desc: <>One profile gives you the ability to both <Text component="span" fw={700}>post tasks</Text> and <Text component="span" fw={700}>apply for projects</Text>.</> },
        { title: 'Limitless Possibilities', desc: <>Find thousands of tasks from one-time errands to complex projects, <Text component="span" fw={700}>develop skills</Text>, and build your rating.</> }
      ]
    },
    youth: {
      title: <>Find work <br /><Text component="span" c="salmon.6" inherit>in your own style</Text></>,
      text: "Don't just flip burgers or pump gas (although that's a great start too). At RU’YA, you can monetize your hobbies. Sports, creativity, knowledge — find an activity that matches your values and rhythm of life.",
      bold: "It's not just hiring. It's a community."
    },
    how: {
      title: 'From idea to result — simple.',
      subtitle: 'We removed all the complexities so you can focus on what matters. See how it works from your side.',
      switch: { client: 'I am looking for talent', freelancer: 'I am looking for work' }
    },
    categories: {
      title: 'Find a specialist for any task',
      subtitle: 'From household errands to complex business projects — discover thousands of opportunities in dozens of categories.'
    },
    adv: {
      title: 'Created for you. To grow with you.',
      subtitle: "RU'YA is more than just a platform. It is your personal tool for achieving goals: from financial independence to self-realization in your favorite field.",
      cards: {
        finance: { title: 'Financial Independence', desc: 'Turn skills into stable income. Work on your own schedule.' },
        reputation: { title: 'Your Reputation is Your Asset', desc: 'Increase your rating to get the best projects.' },
        free: { title: 'Free Registration', desc: 'Create a profile and start searching without investment.' },
        reviews: { title: 'Transparent Reviews', desc: 'Choose the best based on real experience.' },
        verified: { title: 'Work with Verified Users', desc: 'We encourage verification to increase trust.' }
      }
    },
    recruit: {
      title: <>Hire and <Text component="span" c="salmon.6" inherit>develop</Text><br />talent.</>,
      desc: "Whether you are a small local coffee shop or a large chain. With RU'YA, you will easily find motivated specialists ready to work and grow with you.",
      btn: 'Start Search'
    },
    winWin: {
      title: 'Get Help & Give Back',
      subtitle: "Solve your daily challenges while empowering the next generation. It's not just a transaction, it's a contribution.",
      items: [
        {
          title: 'Too busy to handle the routine?',
          desc: "Need groceries delivered, a package picked up, or help moving boxes? Don't stress. There's a student nearby ready to turn your 'to-do' list into their 'done' list.",
          badge: 'Everyday Tasks'
        },
        {
          title: 'Need fresh digital ideas?',
          desc: "Your business needs a logo, or your Instagram needs style? Don't overpay agencies. Hire a talented junior creator who will pour their heart and soul into your project.",
          badge: 'Creative & Tech'
        }
      ]
    },
    scroll: {
      title: 'Stories we help create',
      subtitle: 'Thousands of completed tasks. Thousands of happy users. Here is what they say about us.'
    },
    cta: {
      title: 'Ready to start?',
      desc: "Download the RU'YA app and get access to thousands of opportunities today. Your next project or side job is just a click away."
    },
    footer: {
      slogan: 'Find your way.'
    }
  },
  ru: {
    nav: { about: 'О нас', how: 'Как это работает', adv: 'Преимущества', help: 'Найти помощь', app: 'Скачать приложение' },
    hero: {
      title: <>Начни свое путешествие <br />с RU’YA</>,
      text: <>RU’YA — это современная платформа, созданная для тех, кто хочет действовать, развиваться и зарабатывать. Она объединяет людей с любыми навыками и тех, кому нужна помощь в выполнении задач, услуг или небольших проектов.<br /><br />Здесь частные пользователи и бизнес легко находят исполнителей, а молодёжь и специалисты — возможности для самореализации и дохода.</>,
      btnApp: 'Скачать приложение',
      btnHow: 'Как это работает'
    },
    about: {
      title: 'Мы — RU’YA',
      mainText: <><Text component="span" fw={700} c="salmon.6" inherit>RU’YA</Text> — это больше, чем просто платформа. Это возможность для всех — <Text component="span" fw={700} inherit>найти работу и заработать</Text>.</>,
      subText: <>Находите задания, которые соответствуют вашим <Text component="span" fw={600} c="grayTurquoise.9" inherit>уникальным навыкам</Text> и увлечениям. Это способ для сообщества вдохновлять людей с разными талантами.</>,
      cards: [
        { title: 'Гарантия безопасных сделок', desc: <>Встроенная система <Text component="span" fw={700}>безопасной оплаты</Text> защищает финансы до полного выполнения работы.</> },
        { title: 'Свобода выбора', desc: <>Один профиль дает возможность как <Text component="span" fw={700}>публиковать задачи</Text>, так и <Text component="span" fw={700}>откликаться на проекты</Text>.</> },
        { title: 'Безграничные возможности', desc: <>Находите тысячи заданий от разовых поручений до сложных проектов, <Text component="span" fw={700}>развивайте навыки</Text> и формируйте свой рейтинг.</> }
      ]
    },
    youth: {
      title: <>Найди работу <br /><Text component="span" c="salmon.6" inherit>в своем стиле</Text></>,
      text: 'Не просто жарь бургеры или заправляй машины (хотя и это — отличный старт). В RU’YA ты можешь монетизировать свои увлечения. Спорт, творчество, знания — найди занятие, которое соответствует твоим ценностям и ритму жизни.',
      bold: 'Это не просто найм. Это комьюнити.'
    },
    how: {
      title: 'От идеи до результата — всё просто.',
      subtitle: 'Мы убрали все сложности, чтобы вы могли сосредоточиться на главном. Посмотрите, как это работает с вашей стороны.',
      switch: { client: 'Я ищу исполнителя', freelancer: 'Я ищу работу' }
    },
    categories: {
      title: 'Найдите специалиста для любой задачи',
      subtitle: 'От бытовых поручений до сложных бизнес-проектов — откройте для себя тысячи возможностей в десятках категорий.'
    },
    adv: {
      title: 'Создан для вас. Чтобы расти с вами.',
      subtitle: "RU'YA — это больше, чем просто платформа. Это ваш личный инструмент для достижения целей: от финансовой независимости до самореализации в любимом деле.",
      cards: {
        finance: { title: 'Финансовая независимость', desc: 'Превратите навыки в стабильный доход. Работайте по своему графику.' },
        reputation: { title: 'Ваша репутация — ваш актив', desc: 'Повышайте рейтинг, чтобы получать лучшие проекты.' },
        free: { title: 'Бесплатная регистрация', desc: 'Создайте профиль и начните поиск без вложений.' },
        reviews: { title: 'Прозрачные отзывы', desc: 'Выбирайте лучших, основываясь на реальном опыте.' },
        verified: { title: 'Работайте с проверенными', desc: 'Мы поощряем верификацию для повышения доверия.' }
      }
    },
    recruit: {
      title: <>Нанимайте и <Text component="span" c="salmon.6" inherit>развивайте</Text><br />таланты.</>,
      desc: "Неважно, кто вы — небольшая местная кофейня или крупная сеть. С RU'YA вы легко найдете мотивированных специалистов, готовых работать и расти вместе с вами.",
      btn: 'Начать поиск'
    },
    winWin: {
      title: 'Решайте задачи. Вдохновляйте других.',
      subtitle: "Получайте помощь в делах и одновременно давайте молодежи шанс заработать и получить опыт. Это больше, чем сделка.",
      items: [
        {
          title: 'Завалены рутиной?',
          desc: "Нужно доставить документы, помочь с переездом или закупить продукты? Не тратьте выходные. Рядом есть энергичные исполнители, готовые взять это на себя.",
          badge: 'Бытовые задачи'
        },
        {
          title: 'Нужен свежий взгляд?',
          desc: "Бизнесу нужен логотип или яркий пост в Instagram? Не переплачивайте агентствам. Наймите талантливого новичка, который вложит душу в ваш проект ради портфолио.",
          badge: 'Креатив и IT'
        }
      ]
    },
    scroll: {
      title: 'Истории, которые мы помогаем создавать',
      subtitle: 'Тысячи выполненных задач. Тысячи довольных пользователей. Вот что говорят о нас.'
    },
    cta: {
      title: 'Готовы начать?',
      desc: "Скачайте приложение RU'YA и получите доступ к тысячам возможностей уже сегодня. Ваш следующий проект или подработка — в одном клике."
    },
    footer: {
      slogan: 'Найди свой путь.'
    }
  },
  uz: {
    nav: { about: 'Biz haqimizda', how: 'Bu qanday ishlaydi', adv: 'Afzalliklar', help: 'Yordam olish', app: 'Ilovani yuklab olish' },
    hero: {
      title: <>RU’YA bilan o‘z <br />sayohatingizni boshlang</>,
      text: <>RU’YA — bu harakat qilishni, rivojlanishni va daromad topishni istaganlar uchun yaratilgan zamonaviy platforma. U har qanday ko'nikmaga ega insonlarni va vazifalar, xizmatlar yoki kichik loyihalarni bajarishda yordamga muhtoj bo'lganlarni birlashtiradi.<br /><br />Bu yerda xususiy foydalanuvchilar va biznes ijrochilarni oson topadilar, yoshlar va mutaxassislar esa o'zini namoyon qilish va daromad olish imkoniyatiga ega bo'ladilar.</>,
      btnApp: 'Ilovani yuklab olish',
      btnHow: 'Bu qanday ishlaydi'
    },
    about: {
      title: 'Biz — RU’YA',
      mainText: <><Text component="span" fw={700} c="salmon.6" inherit>RU’YA</Text> — bu shunchaki platforma emas. Bu hamma uchun imkoniyat — <Text component="span" fw={700} inherit>ish topish va daromad qilish</Text>.</>,
      subText: <>Sizning <Text component="span" fw={600} c="grayTurquoise.9" inherit>noyob ko'nikmalaringiz</Text> va qiziqishlaringizga mos keladigan vazifalarni toping. Bu hamjamiyat uchun turli iste'dod egalarini ilhomlantirish usulidir.</>,
      cards: [
        { title: 'Xavfsiz bitimlar kafolati', desc: <>O'rnatilgan <Text component="span" fw={700}>xavfsiz to'lov</Text> tizimi ish to'liq bajarilguniga qadar mablag'laringizni himoya qiladi.</> },
        { title: 'Tanlov erkinligi', desc: <>Bitta profil orqali ham <Text component="span" fw={700}>vazifalar joylashtirish</Text>, ham <Text component="span" fw={700}>loyihalarga taklif yuborish</Text> imkoniyati mavjud.</> },
        { title: 'Cheksiz imkoniyatlar', desc: <>Bir martalik topshiriqlardan tortib murakkab loyihalargacha minglab vazifalarni toping, <Text component="span" fw={700}>ko'nikmalaringizni rivojlantiring</Text> va reytingingizni shakllantiring.</> }
      ]
    },
    youth: {
      title: <>Ishni <br /><Text component="span" c="salmon.6" inherit>o'z uslubingizda</Text> toping</>,
      text: "Shunchaki burger pishirish yoki mashina quyish emas (garchi bu ham ajoyib boshlanish bo'lsa-da). RU'YA da siz o'z hobbilaringizni daromadga aylantirishingiz mumkin. Sport, ijod, bilim — qadriyatlaringiz va hayot ritmingizga mos keladigan mashg'ulotni toping.",
      bold: 'Bu shunchaki yollanish emas. Bu hamjamiyat.'
    },
    how: {
      title: 'Gʻoyadan natijagacha — hammasi oddiy.',
      subtitle: 'Biz barcha qiyinchiliklarni olib tashladik, shunda siz asosiysiga e’tibor qarata olasiz. Bu siz tomondan qanday ishlashini ko‘ring.',
      switch: { client: 'Men ijrochi qidiryapman', freelancer: 'Men ish qidiryapman' }
    },
    categories: {
      title: 'Har qanday vazifa uchun mutaxassis toping',
      subtitle: 'Maishiy yumushlardan tortib murakkab biznes loyihalarigacha — o‘nlab toifalarda minglab imkoniyatlarni kashf eting.'
    },
    adv: {
      title: 'Siz uchun yaratilgan. Siz bilan o\'sish uchun.',
      subtitle: "RU'YA — bu shunchaki platforma emas. Bu moliyaviy mustaqillikdan tortib, sevimli ishingizda o'zingizni namoyon qilishgacha bo'lgan maqsadlarga erishish uchun shaxsiy vositangizdir.",
      cards: {
        finance: { title: 'Moliyaviy mustaqillik', desc: 'Ko‘nikmalaringizni barqaror daromadga aylantiring. O‘z grafigingiz bo‘yicha ishlang.' },
        reputation: { title: 'Sizning obro‘yingiz — sizning aktivingiz', desc: 'Eng yaxshi loyihalarni olish uchun reytingingizni oshiring.' },
        free: { title: 'Bepul ro‘yxatdan o‘tish', desc: 'Profil yarating va sarmoyasiz qidiruvni boshlang.' },
        reviews: { title: 'Shaffof sharhlar', desc: 'Haqiqiy tajribaga asoslanib eng yaxshilarni tanlang.' },
        verified: { title: 'Tasdiqlanganlar bilan ishlang', desc: 'Ishonchni oshirish uchun verifikatsiyani rag‘batlantiramiz.' }
      }
    },
    recruit: {
      title: <>Iqtidorlarni yollang va <Text component="span" c="salmon.6" inherit>rivojlantiring</Text>.</>,
      desc: "Kichik mahalliy qahvaxona yoki yirik tarmoq bo‘lishingizdan qat’i nazar. RU'YA bilan siz bilan ishlashga va o‘sishga tayyor bo‘lgan g‘ayratli mutaxassislarni osongina topasiz.",
      btn: 'Qidiruvni boshlash'
    },
    winWin: {
      title: 'Yordam oling va imkoniyat bering',
      subtitle: "Kundalik vazifalarni hal qiling va yoshlarga daromad topish imkonini bering. Bu shunchaki xizmat emas, bu hissa.",
      items: [
        {
          title: 'Ishlardan charchadingizmi?',
          desc: "Hujjatlarni yetkazish, ko‘chishda yordam yoki bozorlik qilish kerakmi? Dam olish kunlarini sarflamang. Yoningizda g‘ayratli yoshlar bor.",
          badge: 'Kundalik yumushlar'
        },
        {
          title: 'Yangi g‘oyalar kerakmi?',
          desc: "Biznesingiz uchun logotip yoki Instagram uchun post kerakmi? Agentliklarga ortiqcha to‘lamang. O‘z portfoliosi uchun jon kuydirib ishlaydigan iste'dodli yoshlarni yollang.",
          badge: 'Ijod va IT'
        }
      ]
    },
    scroll: {
      title: 'Biz yaratishga yordam beradigan hikoyalar',
      subtitle: 'Minglab bajarilgan vazifalar. Minglab mamnun foydalanuvchilar. Biz haqimizda shunday deyishadi.'
    },
    cta: {
      title: 'Boshlashga tayyormisiz?',
      desc: "RU'YA ilovasini yuklab oling va bugunoq minglab imkoniyatlarga ega bo'ling. Keyingi loyihangiz yoki qo'shimcha daromadingiz — bir tugma uzoqligida."
    },
    footer: {
      slogan: "O'z yo'lingni top."
    }
  }
};

// Helper function to get content based on language
const getData = (lang: Lang) => {
  const tData = {
    ru: {
      clientSteps: [
        { title: 'Опишите вашу задачу', desc: 'Чем подробнее вы опишете, что нужно сделать, тем точнее будут отклики. Укажите сроки и бюджет.' },
        { title: 'Выберите лучшего исполнителя', desc: 'Изучайте профили, портфолио и отзывы. Выбирайте специалиста, который идеально подходит для вашего проекта.' },
        { title: 'Зарезервируйте оплату', desc: 'Деньги хранятся на безопасном счете и переводятся исполнителю только после вашего подтверждения.' },
        { title: 'Примите работу и оставьте отзыв', desc: 'После успешного выполнения задачи подтвердите завершение. Ваш отзыв поможет другим заказчикам.' }
      ],
      freelancerSteps: [
        { title: 'Найдите интересный проект', desc: 'Используйте фильтры и поиск, чтобы найти задачи, которые соответствуют вашим навыкам и ожиданиям по оплате.' },
        { title: 'Отправьте свое предложение', desc: 'Напишите убедительный отклик, чтобы выделиться среди других кандидатов и получить заказ.' },
        { title: 'Выполняйте работу безопасно', desc: 'Будьте уверены в оплате. Средства заказчика уже зарезервированы на платформе и ждут вас.' },
        { title: 'Получите оплату и рейтинг', desc: 'Сдайте работу вовремя, получите честно заработанные деньги и повышайте свой рейтинг для будущих заказов.' }
      ],
      cats: ['Красота и здоровье', 'Мастер на все руки', 'Образование', 'IT', 'Медицинские услуги', 'Право и консалтинг', 'Соцсети и маркетинг', 'Бизнес', 'Кейтеринг', 'Строительство и ремонт', 'Логистика', 'Администрирование', 'Профразвитие', 'Няни и персонал', 'Фото и видео', 'Другое'],
      reviews: [
        { quote: "Эта платформа буквально изменила мой подход к подработке.", text: "" },
        { quote: "Нашла репетитора для сына за один вечер. Быстро, удобно и безопасно!", text: "Раньше поиск специалистов превращался в квест. Здесь я сразу увидела рейтинг, отзывы и выбрала подходящего. Система безопасной сделки — отдельный плюс, чувствуешь себя защищенной." },
        { quote: "Благодаря рейтингу смог быстро набрать постоянных клиентов. Фриланс — мой основной доход.", text: "После пары небольших, но успешно выполненных заданий, мой рейтинг начал расти. Заказчики стали доверять больше, появились более крупные проекты. Главное — делать работу качественно." },
        { quote: "Срочно нужен был переводчик для встречи. Нашел специалиста за час.", text: "Ситуация была критическая. Разместил задачу с пометкой 'срочно', и в течение 20 минут получил три отклика. Выбрал лучшего по профилю, и встреча была спасена. Невероятно эффективно!" }
      ]
    },
    uz: {
      clientSteps: [
        { title: 'Vazifangizni tasvirlab bering', desc: 'Qanchalik batafsil tavsiflasangiz, takliflar shunchalik aniq bo\'ladi. Muddat va byudjetni ko\'rsating.' },
        { title: 'Eng yaxshi ijrochini tanlang', desc: 'Profillar, portfoliolar va sharhlarni o\'rganing. Loyihangiz uchun ideal mutaxassisni tanlang.' },
        { title: 'To\'lovni zaxiralang', desc: 'Pullar xavfsiz hisobda saqlanadi va ijrochiga faqat siz tasdiqlaganingizdan keyin o\'tkaziladi.' },
        { title: 'Ishni qabul qiling va fikr qoldiring', desc: 'Vazifa muvaffaqiyatli bajarilgandan so\'ng yakunlanganini tasdiqlang. Sizning fikringiz boshqa buyurtmachilarga yordam beradi.' }
      ],
      freelancerSteps: [
        { title: 'Qiziqarli loyiha toping', desc: 'Ko\'nikmalaringiz va to\'lov kutilmalaringizga mos keladigan vazifalarni topish uchun filtrlar va qidiruvdan foydalaning.' },
        { title: 'O\'z taklifingizni yuboring', desc: 'Boshqa nomzodlar orasidan ajralib turish va buyurtmani olish uchun ishonchli taklif yozing.' },
        { title: 'Ishni xavfsiz bajaring', desc: 'To\'lovga ishonch hosil qiling. Buyurtmachining mablag\'lari platformada zaxiralangan va sizni kutmoqda.' },
        { title: 'To\'lov va reyting oling', desc: 'Ishni o\'z vaqtida topshiring, halol ishlangan pulni oling va kelajakdagi buyurtmalar uchun reytingingizni oshiring.' }
      ],
      cats: ['Go\'zallik va salomatlik', 'Har ishga usta', 'Ta\'lim', 'IT', 'Tibbiy xizmatlar', 'Huquq va konsalting', 'SMM va Marketing', 'Biznes', 'Keytering', 'Qurilish va ta\'mirlash', 'Logistika', 'Ma\'muriyat', 'Kasbiy rivojlanish', 'Enagalar va xodimlar', 'Foto va video', 'Boshqa'],
      reviews: [
        { quote: "Bu platforma mening qo'shimcha daromadga bo'lgan yondashuvimni butunlay o'zgartirdi.", text: "" },
        { quote: "O'g'lim uchun repetitorni bir oqshomda topdim. Tez, qulay va xavfsiz!", text: "Ilgari mutaxassislarni qidirish qiyin edi. Bu yerda men darhol reyting va sharhlarni ko'rdim va mosini tanladim. Xavfsiz bitim tizimi — alohida plyus, o'zimni himoyalangan his qilaman." },
        { quote: "Reyting tufayli doimiy mijozlarni tezda to'play oldim. Frilans — mening asosiy daromadim.", text: "Bir nechta kichik, ammo muvaffaqiyatli bajarilgan topshiriqlardan so'ng reytingim o'sishni boshladi. Buyurtmachilar ko'proq ishonch bildira boshlashdi, yirikroq loyihalar paydo bo'ldi. Asosiysi — ishni sifatli bajarish." },
        { quote: "Uchrashuv uchun tarjimon zudlik bilan kerak edi. Bir soat ichida mutaxassis topdim.", text: "Vaziyat juda jiddiy edi. Vazifani 'shoshilinch' belgisi bilan joylashtirdim va 20 daqiqa ichida uchta taklif oldim. Profili bo'yicha eng yaxshisini tanladim va uchrashuv qutqarildi. Juda samarali!" }
      ]
    },
    en: {
      clientSteps: [
        { title: 'Describe your task', desc: 'The more details you provide, the more accurate the offers will be. Specify deadlines and budget.' },
        { title: 'Choose the best performer', desc: 'Explore profiles, portfolios, and reviews. Choose a specialist who is perfect for your project.' },
        { title: 'Reserve payment', desc: 'Money is held in a secure account and released to the performer only after your confirmation.' },
        { title: 'Accept work and leave a review', desc: 'After successful completion, confirm the task. Your feedback helps other clients.' }
      ],
      freelancerSteps: [
        { title: 'Find an interesting project', desc: 'Use filters and search to find tasks that match your skills and payment expectations.' },
        { title: 'Send your proposal', desc: 'Write a compelling proposal to stand out from other candidates and get the job.' },
        { title: 'Do the work safely', desc: 'Be sure of payment. Client funds are already reserved on the platform and waiting for you.' },
        { title: 'Get paid and rated', desc: 'Submit work on time, get your hard-earned money, and increase your rating for future orders.' }
      ],
      cats: ['Beauty & Health', 'Handyman', 'Education', 'IT', 'Medical Services', 'Law & Consulting', 'SMM & Marketing', 'Business', 'Catering', 'Construction & Repair', 'Logistics', 'Administration', 'Pro Development', 'Nannies & Staff', 'Photo & Video', 'Other'],
      reviews: [
        { quote: "This platform completely changed my approach to side jobs.", text: "" },
        { quote: "Found a tutor for my son in one evening. Fast, convenient, and safe!", text: "Previously, finding specialists was a quest. Here I immediately saw the rating, reviews, and chose the right one. The safe deal system is a separate plus, I feel protected." },
        { quote: "Thanks to the rating, I quickly gained regular clients. Freelancing is my main income.", text: "After a couple of small but successfully completed tasks, my rating began to grow. Clients began to trust more, larger projects appeared. The main thing is to do the work efficiently." },
        { quote: "Urgently needed a translator for a meeting. Found a specialist in an hour.", text: "The situation was critical. I posted a task marked 'urgent', and within 20 minutes received three responses. I chose the best one by profile, and the meeting was saved. Incredibly effective!" }
      ]
    }
  };

  const currentData = tData[lang];

  const clientSteps = [
    { title: currentData.clientSteps[0].title, description: currentData.clientSteps[0].desc, image: process.env.PUBLIC_URL + '/images/how-it-works/1.PNG' },
    { title: currentData.clientSteps[1].title, description: currentData.clientSteps[1].desc, image: process.env.PUBLIC_URL + '/images/how-it-works/2.PNG' },
    { title: currentData.clientSteps[2].title, description: currentData.clientSteps[2].desc, image: process.env.PUBLIC_URL + '/images/how-it-works/3.PNG' },
    { title: currentData.clientSteps[3].title, description: currentData.clientSteps[3].desc, image: process.env.PUBLIC_URL + '/images/how-it-works/4.PNG' },
  ];

  const freelancerSteps = [
    { title: currentData.freelancerSteps[0].title, description: currentData.freelancerSteps[0].desc, image: process.env.PUBLIC_URL + '/images/how-it-works/freelancer-1.jpeg' },
    { title: currentData.freelancerSteps[1].title, description: currentData.freelancerSteps[1].desc, image: process.env.PUBLIC_URL + '/images/how-it-works/client-2.jpg' },
    { title: currentData.freelancerSteps[2].title, description: currentData.freelancerSteps[2].desc, image: process.env.PUBLIC_URL + '/images/how-it-works/client-3.jpg' },
    { title: currentData.freelancerSteps[3].title, description: currentData.freelancerSteps[3].desc, image: process.env.PUBLIC_URL + '/images/how-it-works/freelancer-1.jpeg' },
  ];

  const categories = [
    { label: currentData.cats[0], icon: IconSparkles },
    { label: currentData.cats[1], icon: IconTools },
    { label: currentData.cats[2], icon: IconSchool },
    { label: currentData.cats[3], icon: IconDeviceLaptop }, // IT
    { label: currentData.cats[4], icon: IconStethoscope },
    { label: currentData.cats[5], icon: IconBriefcase },
    { label: currentData.cats[6], icon: IconChartBar },
    { label: currentData.cats[7], icon: IconBuildingStore },
    { label: currentData.cats[8], icon: IconCooker },
    { label: currentData.cats[9], icon: IconHomeHand },
    { label: currentData.cats[10], icon: IconTruckDelivery },
    { label: currentData.cats[11], icon: IconUsers },
    { label: currentData.cats[12], icon: IconRocket },
    { label: currentData.cats[13], icon: IconUserStar },
    { label: currentData.cats[14], icon: IconCamera },
    { label: currentData.cats[15], icon: IconDots },
  ];

  const testimonials = [
    {
      quote: currentData.reviews[0].quote,
      author: lang === 'uz' ? "Timur N." : lang === 'en' ? "Timur N." : "Тимур Н.",
      role: lang === 'uz' ? "Ijrochi" : lang === 'en' ? "Freelancer" : "Исполнитель",
      avatar: "URL_AVATAR_2", variant: "primary", fullText: currentData.reviews[0].text
    },
    {
      quote: currentData.reviews[1].quote,
      author: lang === 'uz' ? "Aziza K." : lang === 'en' ? "Aziza K." : "Азиза К.",
      role: lang === 'uz' ? "Buyurtmachi" : lang === 'en' ? "Client" : "Заказчик",
      avatar: "URL_AVATAR_1", variant: "secondary", fullText: currentData.reviews[1].text
    },
    {
      quote: currentData.reviews[2].quote,
      author: lang === 'uz' ? "Sardor M." : lang === 'en' ? "Sardor M." : "Сардор М.",
      role: lang === 'uz' ? "Ijrochi" : lang === 'en' ? "Freelancer" : "Исполнитель",
      avatar: "URL_AVATAR_3", variant: "secondary", fullText: currentData.reviews[2].text
    },
    {
      quote: currentData.reviews[3].quote,
      author: lang === 'uz' ? "Oleg P." : lang === 'en' ? "Oleg P." : "Олег П.",
      role: lang === 'uz' ? "Buyurtmachi" : lang === 'en' ? "Client" : "Заказчик",
      avatar: "URL_AVATAR_4", variant: "secondary", fullText: currentData.reviews[3].text
    }
  ];

  return { clientSteps, freelancerSteps, categories, testimonials };
};

// --- COMPONENTS ---

// Horizontal Scroll component for Testimonials section (handles both Mobile and Desktop logic)
const HorizontalScroll = ({ targetRef, testimonials, t }: { targetRef: React.RefObject<HTMLDivElement | null>, testimonials: any[], t: any }) => {
  const { scrollYProgress } = useScroll({
    target: targetRef,
    offset: ["start start", "end end"],
  });

  const x = useTransform(scrollYProgress, [0, 1], ["0%", "-45%"]);

  return (
    <>
      {/* DESKTOP VERSION (Sticky scroll animation) */}
      <Box visibleFrom="md" style={{ position: 'sticky', top: 0, height: '100vh', overflow: 'hidden' }}>
        <Container size="lg" pt={100} pb={150} style={{ position: 'relative' }}>
          <Stack maw={700} gap="md">
            <Title order={2} c="white" fz={{ base: 32, sm: 48 }} lh={1.2}>
              {t.scroll.title}
            </Title>
            <Text size="lg" c="grayTurquoise.2">
              {t.scroll.subtitle}
            </Text>
          </Stack>

          <Box
            visibleFrom="md"
            style={{
              position: 'absolute',
              top: '50px',
              right: 0,
              zIndex: 10
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              animate={{ y: [0, -15, 0], rotate: [0, 5, 0] }}
              transition={{
                y: { duration: 4, repeat: Infinity, ease: "easeInOut" },
                scale: { duration: 0.5 }
              }}
            >
              <Image
                src={process.env.PUBLIC_URL + "/images/mascot-2.png?v=3"}
                alt="Лисенок победитель"
                w={250}
                style={{ filter: 'drop-shadow(0px 20px 40px rgba(0,0,0,0.1))' }}
              />
            </motion.div>
          </Box>
        </Container>
        
        <motion.div style={{ x, display: 'flex', gap: '24px', position: 'absolute', top: '310px', left: 'calc((100vw - 1140px) / 2)' }}>
          {testimonials.map((item, index) => (
            <Paper
              key={index}
              bg={item.variant === 'primary' ? "salmon.6" : "#426457"}
              p={item.variant === 'primary' ? 60 : 40}
              radius="lg"
              style={{
                width: item.variant === 'primary' ? '50vw' : '35vw',
                maxWidth: item.variant === 'primary' ? '700px' : '500px',
                height: '450px',
                borderColor: 'rgba(255, 255, 255, 0.1)',
                borderWidth: item.variant === 'primary' ? 0 : 1,
                borderStyle: 'solid',
                flexShrink: 0
              }}
            >
              <Stack justify="space-between" h="100%">
                  {item.variant === 'primary' ? (
                    <>
                      <IconQuote size={60} color="rgba(255, 255, 255, 0.5)" />
                      <Title order={1} c="white" fz={52} lh={1.1}>{item.quote}</Title>
                    </>
                  ) : (
                    <Stack>
                      <Title order={2} c="white">{item.quote}</Title>
                      <Text c="grayTurquoise.2" mt="md">{item.fullText}</Text>
                    </Stack>
                  )}
                  <Group>
                    <Avatar src={item.avatar} alt={item.author} radius="xl" />
                    <div>
                      <Text c="white" fw={700}>{item.author}</Text>
                      <Text c={item.variant === 'primary' ? "salmon.1" : "grayTurquoise.2"} size="sm">{item.role}</Text>
                    </div>
                  </Group>
                </Stack>
            </Paper>
          ))}
        </motion.div>
      </Box>

      {/* MOBILE VERSION (Native Horizontal Scroll) */}
      <Box hiddenFrom="md" pt={60} pb={80}>
        <Container size="lg">
          <Stack gap="xl">
            <Stack gap="md">
              <Title order={2} c="white" fz={32} lh={1.2}>
                {t.scroll.title}
              </Title>
              <Text size="lg" c="grayTurquoise.2">
                {t.scroll.subtitle}
              </Text>
            </Stack>

            <Box
              mr="-20px"
              style={{
                display: 'flex',
                gap: '16px',
                overflowX: 'auto',
                paddingRight: '20px',
                paddingBottom: '20px',
                scrollSnapType: 'x mandatory',
                WebkitOverflowScrolling: 'touch',
              }}
            >
              {testimonials.map((item, index) => (
                <Paper
                  key={index}
                  bg={item.variant === 'primary' ? "salmon.6" : "#426457"}
                  p="xl"
                  radius="lg"
                  style={{
                    minWidth: '85vw',
                    maxWidth: '350px',
                    scrollSnapAlign: 'center',
                    border: item.variant === 'primary' ? 'none' : '1px solid rgba(255, 255, 255, 0.1)',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <Stack justify="space-between" h="100%" gap="xl">
                    {item.variant === 'primary' ? (
                      <Stack gap="md">
                         <IconQuote size={40} color="rgba(255, 255, 255, 0.5)" />
                         <Title order={2} c="white" fz={28} lh={1.2}>{item.quote}</Title>
                      </Stack>
                    ) : (
                      <Stack gap="md">
                        <Title order={3} c="white" fz={22} lh={1.3}>{item.quote}</Title>
                        <Text c="grayTurquoise.2" size="sm" lineClamp={6}>{item.fullText}</Text>
                      </Stack>
                    )}
                    
                    <Group mt="auto">
                      <Avatar src={item.avatar} alt={item.author} radius="xl" />
                      <div>
                        <Text c="white" fw={700} size="sm">{item.author}</Text>
                        <Text c={item.variant === 'primary' ? "salmon.1" : "grayTurquoise.2"} size="xs">{item.role}</Text>
                      </div>
                    </Group>
                  </Stack>
                </Paper>
              ))}
            </Box>
          </Stack>
        </Container>
      </Box>
    </>
  );
};

// Smooth scroll helper with header offset calculation
const smoothScroll = (id: string) => {
  const element = document.getElementById(id);
  if (element) {
    const headerOffset = 50;
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.scrollY - headerOffset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  }
};

// Application Header with Scroll-based styles and Framer Motion logo
const AppHeader = ({ opened, toggle, lang, setLang }: { opened: boolean; toggle: () => void, lang: Lang, setLang: (l: Lang) => void }) => {
  const t = TRANSLATIONS[lang].nav;
  const [scroll] = useWindowScroll();
  const isHeaderScrolled = scroll.y > 300;

  const { scrollYProgress } = useScroll();
  const rotation = useTransform(scrollYProgress, [0, 1], ["0deg", "1800deg"]);

  return (
    <Box
      component="header"
      style={{
        position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)',
        zIndex: 200, width: '95%', maxWidth: '1200px',
      }}
    >
      <Group h={52} justify="space-between" align="center">
        {/* Left: Logo */}
        <Group
          h="100%"
          px="lg"
          style={(theme) => ({
            backgroundColor: isHeaderScrolled ? 'rgba(255, 255, 255, 0.4)' : 'transparent',
            backdropFilter: isHeaderScrolled ? 'blur(10px)' : 'none',
            WebkitBackdropFilter: isHeaderScrolled ? 'blur(10px)' : 'none',
            border: `1px solid ${isHeaderScrolled ? 'rgba(255, 255, 255, 0.2)' : 'white'}`,
            borderRadius: theme.radius.xl,
            boxShadow: isHeaderScrolled ? '0px 4px 15px rgba(0, 0, 0, 0.1)' : 'none',
            transition: 'all 0.3s ease',
          })}
        >
          <Group
            gap="xs"
            align="center"
            style={{ cursor: 'pointer' }}
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            <motion.div style={{
              rotate: rotation,
              color: isHeaderScrolled ? '#426457' : 'white',
              display: 'flex',
              alignItems: 'center',
              willChange: 'transform'
            }}>
              <RuyaLogo style={{ width: 24, height: 24 }} />
            </motion.div>
            <Image 
              src={ruyaNameLogo} 
              h={20} 
              w="auto" 
              fit="contain"
              style={{
                filter: isHeaderScrolled ? 'brightness(0.4)' : 'none',
                transition: 'filter 0.3s ease'
              }}
            />
          </Group>
        </Group>

        {/* Desktop Navigation */}
        <Group gap="md" visibleFrom="sm" h="100%">
          <Group
            h="100%"
            gap="xl"
            pl="xl"
            pr={8}
            align="center"
            style={(theme) => ({
              backgroundColor: isHeaderScrolled ? 'rgba(255, 255, 255, 0.5)' : 'transparent',
              backdropFilter: isHeaderScrolled ? 'blur(10px)' : 'none',
              WebkitBackdropFilter: isHeaderScrolled ? 'blur(10px)' : 'none',
              border: `1px solid ${isHeaderScrolled ? 'rgba(255, 255, 255, 0.2)' : 'white'}`,
              borderRadius: theme.radius.xl,
              boxShadow: isHeaderScrolled ? '0px 4px 15px rgba(0, 0, 0, 0.1)' : 'none',
              transition: 'all 0.3s ease',
            })}
          >
            <Anchor href="#about" onClick={(e) => { e.preventDefault(); smoothScroll('about'); }} c={isHeaderScrolled ? 'grayTurquoise.8' : 'white'}>{t.about}</Anchor>
            <Anchor href="#how-it-works" onClick={(e) => { e.preventDefault(); smoothScroll('how-it-works'); }} c={isHeaderScrolled ? 'grayTurquoise.8' : 'white'}>{t.how}</Anchor>
            <Anchor href="#advantages" onClick={(e) => { e.preventDefault(); smoothScroll('advantages'); }} c={isHeaderScrolled ? 'grayTurquoise.8' : 'white'}>{t.adv}</Anchor>
            <Anchor href="#win-win" onClick={(e) => { e.preventDefault(); smoothScroll('win-win'); }} c={isHeaderScrolled ? 'grayTurquoise.8' : 'white'}>{t.help}</Anchor>
            
            {/* Language Switcher */}
            <SegmentedControl
              value={lang}
              onChange={(val) => setLang(val as Lang)}
              data={[
                {
                  value: 'ru',
                  label: (
                    <Text span fw={500} size="xs" c={lang === 'ru' || isHeaderScrolled ? '#426457' : 'white'}>RU</Text>
                  )
                },
                {
                  value: 'uz',
                  label: (
                    <Text span fw={500} size="xs" c={lang === 'uz' || isHeaderScrolled ? '#426457' : 'white'}>UZ</Text>
                  )
                },
                {
                  value: 'en',
                  label: (
                    <Text span fw={500} size="xs" c={lang === 'en' || isHeaderScrolled ? '#426457' : 'white'}>EN</Text>
                  )
                }
              ]}
              size="xs"
              radius="xl"
              bg="transparent"
              withItemsBorders={false}
              styles={{
                root: {
                  backgroundColor: isHeaderScrolled ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.2)',
                  border: isHeaderScrolled ? 'none' : '1px solid rgba(255,255,255,0.3)'
                },
                indicator: {
                  backgroundColor: 'white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                },
                label: {
                  padding: '4px 10px'
                }
              }}
            />
          </Group>

          <Button
            onClick={() => smoothScroll('cta')}
            h="100%"
            radius="xl"
            size="md"
            color={isHeaderScrolled ? "salmon.6" : "white"}
            variant={isHeaderScrolled ? 'filled' : 'outline'}
            style={{
              boxShadow: isHeaderScrolled ? '0 8px 25px rgba(255, 117, 58, 0.3)' : 'none',
              transition: 'all 0.3s ease',
              border: isHeaderScrolled ? 'none' : '1px solid white',
            }}
            styles={{
              root: {
                '&:hover': {
                  transform: isHeaderScrolled ? 'translateY(-2px)' : 'none',
                }
              }
            }}
            py="md"
            rightSection={<IconDownload size={16} />}
          >
            {t.app}
          </Button>
        </Group>

        {/* Mobile: Burger Menu */}
        <Group
          h="100%"
          px="sm"
          hiddenFrom="sm"
          align="center"
          style={(theme) => ({
            backgroundColor: isHeaderScrolled ? 'rgba(255, 255, 255, 0.4)' : 'transparent',
            backdropFilter: isHeaderScrolled ? 'blur(10px)' : 'none',
            WebkitBackdropFilter: isHeaderScrolled ? 'blur(10px)' : 'none',
            border: `1px solid ${isHeaderScrolled ? 'rgba(255, 255, 255, 0.2)' : 'white'}`,
            borderRadius: theme.radius.xl,
            boxShadow: isHeaderScrolled ? '0px 4px 15px rgba(0, 0, 0, 0.1)' : 'none',
            transition: 'all 0.3s ease',
          })}
        >
          <Burger
            opened={opened}
            onClick={toggle}
            size="sm"
            color={isHeaderScrolled ? 'grayTurquoise.8' : 'white'}
          />
        </Group>
      </Group>
    </Box>
  );
};

// --- MAIN LANDING PAGE COMPONENT ---
const LandingPage = () => {
  const [opened, { toggle }] = useDisclosure();
  const targetRef = useRef<HTMLDivElement | null>(null);

  const [lang, setLang] = useState<Lang>('ru');
  const t = TRANSLATIONS[lang];
  const { clientSteps, freelancerSteps, categories, testimonials } = getData(lang);
  const row1 = categories.slice(0, 8);
  const row2 = categories.slice(8, 16);

  const [userType, setUserType] = useState('client');
  const activeSteps = userType === 'client' ? clientSteps : freelancerSteps;

  useEffect(() => {
    // Dynamic page title
    const titles: Record<Lang, string> = {
      ru: "RU'YA — Найди работу в своем стиле",
      uz: "RU'YA — Ishni o'z uslubingizda toping",
      en: "RU'YA — Find work in your own style"
    };
    document.title = titles[lang];

    // Dynamic Favicon
    const link = document.querySelector("link[rel~='icon']") as HTMLLinkElement || document.createElement('link');
    link.type = 'image/png';
    link.rel = 'icon';
    link.href = process.env.PUBLIC_URL + '/favicon.png';
    document.head.appendChild(link);
  }, [lang]);
  // Framer Motion Variants
  const heroContainerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2, delayChildren: 0.3 },
    },
  };

  const heroItemVariants: Variants = {
    hidden: { opacity: 0, y: 30, filter: 'blur(10px)' },
    visible: {
      opacity: 1, y: 0, filter: 'blur(0px)',
      transition: { duration: 0.8, ease: [0.25, 1, 0.5, 1] },
    },
  };

  const svgContainerVariants: Variants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.5 } },
  };

  const eraserPathVariants: Variants = {
    hidden: { pathLength: 1, pathOffset: 0 },
    visible: {
      pathLength: 1, pathOffset: 1,
      transition: { duration: 1.5, ease: 'easeInOut' },
    },
  };

  const staggerContainerVariants: Variants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.15 } },
  };

  const fadeInUpVariants: Variants = {
    hidden: { opacity: 0, y: 40, filter: 'blur(8px)' },
    visible: {
      opacity: 1, y: 0, filter: 'blur(0px)',
      transition: { duration: 0.7, ease: "easeOut" },
    },
  };

  return (
    <AppShell
      navbar={{
        width: '100%',
        breakpoint: 'sm',
        collapsed: { desktop: true, mobile: !opened }
      }}
      padding={0}
    >
      <AppHeader opened={opened} toggle={toggle} lang={lang} setLang={setLang} />

      {/* Mobile Menu */}
      <AppShell.Navbar
        zIndex={190}
        py="xl"
        px="md"
        style={{
          backgroundColor: '#426457',
          border: 'none',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          overflow: 'hidden'
        }}
      >
        {/* Background Watermark */}
        <Text
          style={{
            position: 'absolute', bottom: '-5%', right: '-10%',
            fontSize: '30vh', fontWeight: 900, color: 'rgba(255, 255, 255, 0.03)',
            lineHeight: 1, zIndex: 0, pointerEvents: 'none'
          }}
        >
          RU'YA
        </Text>

        <Container size="sm" w="100%" style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between' }}>
          {/* Menu Links */}
          <Stack gap="xl" mt={80} align="center">
            {[
              { label: t.nav.about, href: '#about' },
              { label: t.nav.how, href: '#how-it-works' },
              { label: t.nav.adv, href: '#advantages' },
              { label: t.nav.help, href: '#win-win' }
            ].map((link, index) => (
              <motion.div
                key={link.href}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Anchor
                  href={link.href}
                  onClick={(e) => { e.preventDefault(); toggle(); smoothScroll(link.href.replace('#', '')); }}
                  underline="never"
                >
                  <Title
                    order={2}
                    c="white"
                    ta="center"
                    style={{
                      fontSize: 'clamp(2rem, 8vw, 3.5rem)',
                      fontWeight: 800,
                      letterSpacing: '-1px'
                    }}
                  >
                    {link.label}
                  </Title>
                </Anchor>
              </motion.div>
            ))}
          </Stack>

          {/* Language & CTA */}
          <Stack gap="xl" mb="xl">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
            >
              <Group justify="center">
                <SegmentedControl
                  key={opened ? 'menu-open' : 'menu-closed'}
                  value={lang}
                  onChange={(val) => setLang(val as Lang)}
                  data={[
                    { label: 'Русский', value: 'ru' },
                    { label: "O'zbek", value: 'uz' },
                    { label: "English", value: 'en' }
                  ]}
                  size="lg"
                  radius="xl"
                  fullWidth
                  color="salmon.6"
                  bg="rgba(255, 255, 255, 0.1)"
                  styles={{
                    root: {
                      border: '1px solid rgba(255,255,255,0.2)',
                      padding: 4,
                      height: 'auto'
                    },
                    label: {
                      color: 'white',
                      fontWeight: 600,
                      paddingTop: 8,
                      paddingBottom: 8
                    },
                    indicator: {
                      boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
                    }
                  }}
                />
              </Group>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <Button
                fullWidth
                size="xl"
                h={64}
                radius="xl"
                color="white"
                variant="white"
                c="dark"
                onClick={() => { toggle(); smoothScroll('cta'); }}
                rightSection={<IconDownload size={24} />}
                style={{
                  fontSize: '1.2rem',
                  fontWeight: 700,
                  boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
                }}
              >
                {t.nav.app}
              </Button>
            </motion.div>
          </Stack>
        </Container>
      </AppShell.Navbar>

      <AppShell.Main>
        {/* HERO SECTION */}
        <Box
          id="hero"
          style={{
            position: 'relative', height: '100vh', width: '100%',
            overflow: 'hidden', display: 'flex', alignItems: 'flex-end',
            justifyContent: 'center',
          }}
        >
          <video
            autoPlay loop muted playsInline preload="auto"
            poster={process.env.PUBLIC_URL + "/images/hero-poster.jpg"}
            style={{
              position: 'absolute', top: '50%', left: '50%',
              width: '100%', height: '100%', objectFit: 'cover',
              transform: 'translate(-50%, -50%)', zIndex: 1,
            }}
          >
            <source src={process.env.PUBLIC_URL + "/videos/hero-background.mp4"} type="video/mp4" />
            Your browser does not support video.
          </video>
          <Box style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.4)', zIndex: 2 }} />

          <Box pb="md" style={{ width: '95%', maxWidth: '1200px', zIndex: 3, position: 'relative' }}>
            <motion.div
              variants={heroContainerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: false, amount: 0.4 }}
            >
              <Stack align="flex-start" ta="left" gap="lg">
                <motion.div variants={heroItemVariants}>
                  <Title order={1} c="white" fz={{ base: 32, xs: 38, sm: 52, md: 56, lg: 60 }} lh={1.0}>
                    {t.hero.title}
                  </Title>
                </motion.div>

                <motion.div variants={heroItemVariants}>
                  <Text size="lg" maw={{ base: '100%', md: 600 }} c="white" lh={1.3}>
                    {t.hero.text}
                  </Text>
                </motion.div>

                <motion.div variants={heroItemVariants} style={{ marginTop: 'var(--mantine-spacing-xl)' }}>
                  <Group gap="md">
                    <Button
                      onClick={() => smoothScroll('cta')}
                      color="salmon.6"
                      size="lg"
                      radius="xl"
                      rightSection={<IconDownload size={18} />}
                      style={{ boxShadow: '0 10px 30px rgba(255, 117, 58, 0.35)', transition: 'all 0.3s ease' }}
                      styles={{ root: { '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 20px 40px rgba(255, 117, 58, 0.5)' } } }}
                    >
                      {t.hero.btnApp}
                    </Button>
                    <Button
                      onClick={(e) => { e.preventDefault(); smoothScroll('how-it-works'); }}
                      variant="outline" color="white" size="lg" radius="xl" style={{ borderColor: 'white' }}
                    >
                      {t.hero.btnHow}
                    </Button>
                  </Group>
                </motion.div>
              </Stack>
            </motion.div>
          </Box>
        </Box>

        <Stack gap="xl" style={{ backgroundColor: '#f4f4db', overflow: 'visible' }}>
          <Box style={{ position: 'relative', overflow: 'visible' }}>
            {/* ABOUT SECTION */}
            <Box id="about" py="md">
              <Container size="lg">
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: false, amount: 0.2 }}
                  variants={staggerContainerVariants}
                >
                  <Stack gap={0}>
                    <Grid gutter={{ base: 20, sm: 40, md: 60 }} align="center">
                      <Grid.Col span={{ base: 12, md: 7 }}>
                        <motion.div variants={fadeInUpVariants}>
                          <Stack gap="md">
                            <Title order={2} c="grayTurquoise.8" fz={{ base: 28, sm: 36, md: 48, lg: 52 }} lh={1.2}>
                              {t.about.title}
                            </Title>
                            <Stack gap="md">
                              <Text size="lg" c="grayTurquoise.9" lh={1.6}>
                                {t.about.mainText}
                              </Text>
                              <Text size="lg" c="grayTurquoise.7" lh={1.6}>
                                {t.about.subText}
                              </Text>
                            </Stack>
                          </Stack>
                        </motion.div>
                      </Grid.Col>

                      <Grid.Col span={{ base: 12, md: 5 }} visibleFrom="sm">
                        <motion.div variants={fadeInUpVariants}>
                          <Box style={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
                            <motion.div
                              style={{
                                color: '#426457',
                                opacity: 0.5,
                                filter: 'drop-shadow(0px 5px 10px rgba(0,0,0,0.05))',
                                transformOrigin: 'center center',
                                width: '100%',
                                maxWidth: '250px',
                              }}
                              initial={{ rotate: 0, y: 0 }}
                              animate={{ y: [-15, 0, -15] }}
                              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                            >
                              <RuyaLogo style={{ width: '100%', height: 'auto' }} />
                            </motion.div>
                          </Box>
                        </motion.div>
                      </Grid.Col>
                    </Grid>

                    <SimpleGrid cols={{ base: 1, sm: 3 }} spacing={{ base: 'md', sm: 'lg', md: 'xl' }} mt="md">
                      {t.about.cards.map((card, idx) => (
                        <motion.div variants={fadeInUpVariants} style={{ height: '100%' }} key={idx}>
                          <Paper
                            withBorder radius="lg" shadow="sm" h="100%"
                            style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
                          >
                            <Stack pt="md" px="md" pb="md" gap="md" style={{ flex: 1 }}>
                              <Title order={2} c="grayTurquoise.8" fz={{ base: 20, sm: 22, md: 24 }} lh={1.2}>{card.title}</Title>
                              <Text size="lg" c="dimmed">{card.desc}</Text>
                            </Stack>
                            <Image
                              src={process.env.PUBLIC_URL + `/images/${['trust', 'same', 'offers'][idx]}.png`}
                              h={{ base: 120, sm: 150, md: 180 }}
                              alt="Illustration"
                            />
                          </Paper>
                        </motion.div>
                      ))}
                    </SimpleGrid>
                  </Stack>
                </motion.div>
              </Container>
            </Box>

            {/* YOUTH SECTION */}
            <Box
              id="youth"
              style={{
                position: 'relative', height: 'auto', width: '100%', overflow: 'hidden',
              }}
            >
              {/* Custom CSS for background animation */}
              <style>{`
                @keyframes scrollBgPos {
                  from { transform: translate3d(0, 0, 0); }
                  to { transform: translate3d(-50%, 0, 0); }
                }
                .chat-bubble { position: relative; }
                .chat-bubble::after {
                  content: ''; position: absolute; left: 24px; bottom: -15px; width: 0; height: 0;
                  border: 30px solid transparent; border-top-color: #F8F9FA; border-bottom: 0; border-left: 0;
                }
              `}</style>

              <Box
                style={{
                  position: 'absolute', top: 0, left: 0, height: '100%', width: '200%',
                  backgroundImage: `url(${process.env.PUBLIC_URL + "/images/teenagers.png"})`,
                  backgroundRepeat: 'repeat-x', backgroundSize: 'auto 100%',
                  animation: 'scrollBgPos 60s linear infinite', willChange: 'transform', zIndex: 0,
                }}
              />
              <Box style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.1)', zIndex: 1 }} />

              <Container size="lg" style={{ position: 'relative', zIndex: 2, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }} py="md">
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: 20 }}
                  whileInView={{ opacity: 1, scale: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.5 }}
                  transition={{ duration: 0.6 }}
                  style={{ width: '100%', maxWidth: '700px' }}
                >
                  <Paper
                    bg="#426457"
                    p={{ base: 'md', sm: 'lg', md: 40, lg: 50 }}
                    radius="xl"
                    shadow="xl"
                    style={{ textAlign: 'left', border: '1px solid rgba(255, 255, 255, 0.1)' }}
                  >
                    <Stack gap="md" align="flex-start">
                      <Title order={2} c="white" fz={{ base: 28, sm: 36, md: 48, lg: 54 }} lh={1.1}>
                        {t.youth.title}
                      </Title>
                      <Text size="lg" c="grayTurquoise.2" lh={1.5}>
                        {t.youth.text}
                      </Text>
                      <Text size="lg" c="white" fw={700}>
                        {t.youth.bold}
                      </Text>
                    </Stack>
                  </Paper>
                </motion.div>
              </Container>
            </Box>

            {/* HOW IT WORKS SECTION */}
            <Box
              id="how-it-works"
              bg="#426457"
              py="md"
              style={{ position: 'relative', overflow: 'hidden' }}
            >
              <Container size="lg">
                <Stack gap="md">
                  <Grid gutter={{ base: 20, sm: 30, md: 40 }} align="center">
                    <Grid.Col span={{ base: 12, md: 8 }} style={{ position: 'relative', zIndex: 2 }}>
                      <Stack gap="md" align="flex-start">
                        <Stack gap="md">
                          <Title order={2} c="white" fz={{ base: 28, sm: 36, md: 44, lg: 48 }} lh={1.2}>
                            {t.how.title}
                          </Title>
                          <Text size="lg" c="grayTurquoise.2">
                            {t.how.subtitle}
                          </Text>
                        </Stack>

                        <SegmentedControl
                          value={userType}
                          onChange={setUserType}
                          data={[
                            { label: t.how.switch.client, value: 'client' },
                            { label: t.how.switch.freelancer, value: 'freelancer' },
                          ]}
                          color="salmon.6"
                          radius="lg"
                          size="lg"
                        />
                      </Stack>
                    </Grid.Col>
                  </Grid>

                  <Stack gap="md">
                    {activeSteps.map((step, index) => {
                      const isReversed = index % 2 !== 0;
                      const variants: Variants = {
                        hidden: { opacity: 0, scale: 0.95 },
                        visible: { opacity: 1, scale: 1, transition: { duration: 0.6, ease: "easeOut" } },
                      };

                      return (
                        <motion.div
                          key={step.title}
                          initial="hidden"
                          whileInView="visible"
                          viewport={{ once: false, amount: 0.8 }}
                          style={{ position: 'relative', zIndex: index }}
                        >
                          {/* Animated Arrow Connector */}
                          {index < activeSteps.length - 1 && (
                            <Box
                              pos="absolute" top="80%" left="50%"
                              visibleFrom="md"
                              style={{ transform: `translateX(-50%) scaleX(${isReversed ? -1 : 1})` }}
                              w={{ base: 300, md: 500 }} h={{ base: 120, md: 180 }}
                            >
                              <motion.svg
                                viewBox="0 0 500 180"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                style={{ overflow: 'visible' }}
                                variants={svgContainerVariants}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: false, amount: 0.3, margin: "-50px" }}
                              >
                                <path d="M250 0 C 400 0, 400 180, 500 180" stroke="#FF753A" strokeWidth="3" strokeDasharray="4 8" />
                                <motion.path d="M250 0 C 400 0, 400 180, 500 180" stroke="#426457" strokeWidth="6" variants={eraserPathVariants} />
                              </motion.svg>
                            </Box>
                          )}

                          <Grid gutter={{ base: 20, sm: 30, md: 40 }} align="center">
                            <Grid.Col span={{ base: 12, sm: 5 }} order={{ sm: isReversed ? 2 : 1 }}>
                              <motion.div variants={variants}>
                                <Image
                                  src={step.image} alt={step.title} radius="lg" maw={{ base: 200, sm: 250, md: 280 }} mx="auto"
                                  style={{
                                    boxShadow: '0px 10px 25px rgba(0, 0, 0, 0.25)',
                                    border: '2px solid rgba(255, 255, 255, 0.1)',
                                    transform: 'translateZ(0)', willChange: 'transform', backfaceVisibility: 'hidden',
                                  }}
                                />
                              </motion.div>
                            </Grid.Col>

                            <Grid.Col span={{ base: 12, sm: 7 }} order={{ sm: isReversed ? 1 : 2 }}>
                              <motion.div variants={variants} style={{ position: 'relative' }}>
                                <Text
                                  c="rgba(255, 255, 255, 0.05)" fz={{ base: 60, sm: 80, md: 100, lg: 120 }} fw={800} lh={1}
                                  visibleFrom="sm"
                                  style={{
                                    position: 'absolute', top: '50%',
                                    left: isReversed ? 'auto' : '-10px', right: isReversed ? '-10px' : 'auto',
                                    transform: 'translateY(-50%)', zIndex: 0, userSelect: 'none',
                                  }}
                                >
                                  0{index + 1}
                                </Text>

                                <Stack gap="md" style={{ position: 'relative', zIndex: 1 }}>
                                  <Title order={3} c="white" fz={{ base: 20, sm: 24, md: 28, lg: 32 }} lh={1.2}>
                                    {step.title}
                                  </Title>
                                  <Text c="grayTurquoise.2" size="md">
                                    {step.description}
                                  </Text>
                                </Stack>
                              </motion.div>
                            </Grid.Col>
                          </Grid>
                        </motion.div>
                      );
                    })}
                  </Stack>
                </Stack>
              </Container>
              <motion.div
                initial={{ x: '100%' }}
                whileInView={{ x: '0%' }}
                viewport={{ once: false }}
                transition={{ type: 'spring', stiffness: 80, damping: 20, delay: 0.2 }}
                style={{
                  position: 'absolute', top: '70px', right: 0, zIndex: 1, display: 'flex',
                }}
              >
                <Image
                  src={process.env.PUBLIC_URL + "/images/mascot-corner.png?v=6"}
                  alt="Лисенок выглядывает"
                  w={{ base: 0, sm: 180, md: 220, lg: 250 }}
                  style={{ filter: 'drop-shadow(-20px 10px 30px rgba(0,0,0,0.1))' }}
                />
              </motion.div>
            </Box>

            {/* CATEGORIES SECTION */}
            <Box id="categories" pt="md" pb="md" style={{ overflow: 'hidden' }}>
              <style>{`
                @keyframes scrollLeft { from { transform: translateX(0); } to { transform: translateX(-50%); } }
                @keyframes scrollRight { from { transform: translateX(-50%); } to { transform: translateX(0); } }
                .carousel-track-left { display: flex; width: max-content; animation: scrollLeft 80s linear infinite; }
                .carousel-track-right { display: flex; width: max-content; animation: scrollRight 80s linear infinite; }
              `}</style>

              <Container size="lg">
                <Stack gap="md">
                  <Stack maw={700} gap="md">
                    <Title order={2} c="grayTurquoise.8" fz={{ base: 28, sm: 36, md: 44, lg: 48 }} lh={1.2}>
                      {t.categories.title}
                    </Title>
                    <Text size="lg" c="dimmed">
                      {t.categories.subtitle}
                    </Text>
                  </Stack>

                  <Stack gap="md" mt="md" style={{ marginLeft: 'calc(-50vw + 50%)', width: '100vw', maxWidth: '100vw' }}>
                    <div className="carousel-track-left">
                      {[...row1, ...row1].map((category, index) => (
                        <Paper key={index} shadow="sm" radius="lg" withBorder p="md" mx="sm" style={{ minWidth: window.innerWidth < 768 ? '220px' : '280px' }}>
                          <Group gap="md">
                            <ThemeIcon color="salmon.6" variant="light" size={48} radius="md">
                              <category.icon size={window.innerWidth < 768 ? 20 : 28} />
                            </ThemeIcon>
                            <Text fw={500} size="md">{category.label}</Text>
                          </Group>
                        </Paper>
                      ))}
                    </div>

                    <div className="carousel-track-right">
                      {[...row2, ...row2].map((category, index) => (
                        <Paper key={index} shadow="sm" radius="lg" withBorder p="md" mx="sm" style={{ minWidth: window.innerWidth < 768 ? '220px' : '280px' }}>
                          <Group gap="md">
                            <ThemeIcon color="salmon.6" variant="light" size={48} radius="md">
                              <category.icon size={window.innerWidth < 768 ? 20 : 28} />
                            </ThemeIcon>
                            <Text fw={500} size="md">{category.label}</Text>
                          </Group>
                        </Paper>
                      ))}
                    </div>
                  </Stack>
                </Stack>
              </Container>
            </Box>

            {/* ADVANTAGES (BENTO GRID) */}
            <Box id="advantages" bg="#f4f4db" pt="md">
              <Container size="lg">
                <Stack gap="md">
                  <motion.div
                    initial="hidden" whileInView="visible" viewport={{ once: false, amount: 0.5 }}
                    variants={fadeInUpVariants}
                  >
                    <Stack maw={700} gap="md">
                      <Title order={2} c="grayTurquoise.8" fz={{ base: 28, sm: 36, md: 44, lg: 48 }} lh={1.2}>
                        {t.adv.title}
                      </Title>
                      <Text size="lg" c="dimmed">
                        {t.adv.subtitle}
                      </Text>
                    </Stack>
                  </motion.div>

                  <motion.div
                    initial="hidden" whileInView="visible"
                    viewport={{ once: false, amount: 0.1, margin: "-100px" }}
                    variants={staggerContainerVariants}
                  >
                    <Grid gutter={{ base: 'md', sm: 'lg', md: 'xl' }}>
                      {/* Photo 1 */}
                      <Grid.Col span={{ base: 12, md: 7 }}>
                        <motion.div variants={fadeInUpVariants} style={{ height: '100%' }}>
                          <Paper radius="lg" p={0} style={{ overflow: 'hidden', height: '100%' }}>
                            <Image src={process.env.PUBLIC_URL + "/images/bento-1.png"} alt="Designer at work" h={{ base: 250, sm: 300, md: 400 }} fit="cover" />
                          </Paper>
                        </motion.div>
                      </Grid.Col>

                      {/* Card 1: Finance */}
                      <Grid.Col span={{ base: 12, md: 5 }}>
                        <motion.div variants={fadeInUpVariants} style={{ height: '100%' }}>
                          <Paper bg="#426457" radius="lg" p={{ base: 'md', sm: 'lg', md: 'xl' }} style={{ height: '100%', position: 'relative', overflow: 'hidden' }}>
                            <Box visibleFrom="md"><IconWallet size={160} style={{ position: 'absolute', bottom: 20, right: 20, opacity: 0.1, color: 'white' }} /></Box>
                            <Stack h="100%" justify="flex-end" style={{ zIndex: 1 }}>
                              <Title order={2} fz={{ base: 24, sm: 32, md: 38, lg: 44 }} c="white" lh={1.1}>{t.adv.cards.finance.title}</Title>
                              <Text size="lg" c="grayTurquoise.2" mt="sm">{t.adv.cards.finance.desc}</Text>
                            </Stack>
                          </Paper>
                        </motion.div>
                      </Grid.Col>

                      {/* Card 2: Reputation */}
                      <Grid.Col span={{ base: 12, md: 4 }}>
                        <motion.div variants={fadeInUpVariants} style={{ height: '100%' }}>
                          <Paper bg="#426457" radius="lg" p={{ base: 'md', sm: 'lg', md: 'xl' }} style={{ height: '100%', position: 'relative', overflow: 'hidden' }}>
                            <Box visibleFrom="md"><IconAward size={140} style={{ position: 'absolute', bottom: 15, right: 15, opacity: 0.1, color: 'white' }} /></Box>
                            <Stack h="100%" justify="flex-end" style={{ zIndex: 1 }}>
                              <Title order={2} fz={{ base: 24, sm: 32, md: 38, lg: 44 }} c="white" lh={1.1}>{t.adv.cards.reputation.title}</Title>
                              <Text size="lg" c="grayTurquoise.2" mt="sm">{t.adv.cards.reputation.desc}</Text>
                            </Stack>
                          </Paper>
                        </motion.div>
                      </Grid.Col>

                      {/* Photo 2 */}
                      <Grid.Col span={{ base: 12, md: 4 }}>
                        <motion.div variants={fadeInUpVariants} style={{ height: '100%' }}>
                          <Paper radius="lg" p={0} style={{ overflow: 'hidden', height: '100%', minHeight: '200px' }}>
                            <Image src={process.env.PUBLIC_URL + "/images/bento-2.png"} alt="Photographer" h="100%" fit="cover" />
                          </Paper>
                        </motion.div>
                      </Grid.Col>

                      {/* Photo 3 */}
                      <Grid.Col span={{ base: 12, md: 4 }}>
                        <motion.div variants={fadeInUpVariants} style={{ height: '100%' }}>
                          <Paper radius="lg" p={0} style={{ overflow: 'hidden', height: '100%', minHeight: '200px' }}>
                            <Image src={process.env.PUBLIC_URL + "/images/bento-3.png"} alt="Tutor" h="100%" fit="cover" />
                          </Paper>
                        </motion.div>
                      </Grid.Col>

                      {/* Card 3: Free */}
                      <Grid.Col span={{ base: 12, md: 4 }}>
                        <motion.div variants={fadeInUpVariants} style={{ height: '100%' }}>
                          <Paper bg="#426457" radius="lg" p={{ base: 'md', sm: 'lg', md: 'xl' }} style={{ height: '100%', position: 'relative', overflow: 'hidden' }}>
                            <Box visibleFrom="md"><IconGift size={120} style={{ position: 'absolute', bottom: 10, right: 15, opacity: 0.1, color: 'white' }} /></Box>
                            <Stack h="100%" justify="flex-end" style={{ zIndex: 1 }}>
                              <Title order={3} fz={{ base: 22, sm: 26, md: 28, lg: 32 }} c="white" lh={1.2}>{t.adv.cards.free.title}</Title>
                              <Text size="lg" c="grayTurquoise.2" mt="sm">{t.adv.cards.free.desc}</Text>
                            </Stack>
                          </Paper>
                        </motion.div>
                      </Grid.Col>

                      {/* Card 4: Reviews */}
                      <Grid.Col span={{ base: 12, md: 4 }}>
                        <motion.div variants={fadeInUpVariants} style={{ height: '100%' }}>
                          <Paper bg="#426457" radius="lg" p={{ base: 'md', sm: 'lg', md: 'xl' }} style={{ height: '100%', position: 'relative', overflow: 'hidden' }}>
                            <Box visibleFrom="md"><IconMessageStar size={120} style={{ position: 'absolute', bottom: 10, right: 15, opacity: 0.1, color: 'white' }} /></Box>
                            <Stack h="100%" justify="flex-end" style={{ zIndex: 1 }}>
                              <Title order={3} fz={{ base: 22, sm: 26, md: 28, lg: 32 }} c="white" lh={1.2}>{t.adv.cards.reviews.title}</Title>
                              <Text size="lg" c="grayTurquoise.2" mt="sm">{t.adv.cards.reviews.desc}</Text>
                            </Stack>
                          </Paper>
                        </motion.div>
                      </Grid.Col>

                      {/* Card 5: Verified */}
                      <Grid.Col span={{ base: 12, md: 4 }}>
                        <motion.div variants={fadeInUpVariants} style={{ height: '100%' }}>
                          <Paper bg="#426457" radius="lg" p={{ base: 'md', sm: 'lg', md: 'xl' }} style={{ height: '100%', position: 'relative', overflow: 'hidden' }}>
                            <Box visibleFrom="md"><IconTrophy size={120} style={{ position: 'absolute', bottom: 10, right: 15, opacity: 0.1, color: 'white' }} /></Box>
                            <Stack h="100%" justify="flex-end" style={{ zIndex: 1 }}>
                              <Title order={3} fz={{ base: 22, sm: 26, md: 28, lg: 32 }} c="white" lh={1.2}>{t.adv.cards.verified.title}</Title>
                              <Text size="lg" c="grayTurquoise.2" mt="sm">{t.adv.cards.verified.desc}</Text>
                            </Stack>
                          </Paper>
                        </motion.div>
                      </Grid.Col>
                    </Grid>
                  </motion.div>
                </Stack>
              </Container>
            </Box>

            {/* RECRUIT SECTION */}
            <Box id="recruit" bg="lightYellow.5" py="md" style={{ position: 'relative', overflow: 'hidden' }}>
              <Box pos="absolute" top={0} left={0} bottom={0} w="50%" style={{ backgroundImage: 'radial-gradient(rgba(0,0,0, 0.05) 1px, transparent 1px)', backgroundSize: '20px 20px', opacity: 0.5, }} hiddenFrom="sm" />
              <Container size="lg">
                <Grid align="center" gutter={{ base: 30, sm: 40, md: 60 }}>
                  <Grid.Col span={{ base: 12, md: 6 }} style={{ position: 'relative', zIndex: 2 }}>
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: false, amount: 0.3 }}
                      transition={{ duration: 0.8 }}
                    >
                      <Stack gap="md">
                        <Title order={1} c="grayTurquoise.8" fz={{ base: 32, sm: 44, md: 54, lg: 60 }} lh={1.1}>
                          {t.recruit.title}
                        </Title>
                        <Text c="dimmed" size="md" maw={{ base: '100%', md: 500 }}>
                          {t.recruit.desc}
                        </Text>
                        <Button onClick={() => smoothScroll('cta')} mt="md" color="salmon.6" size="md" radius="xl" rightSection={<IconArrowRight size={18} />} style={{ alignSelf: 'flex-start' }}>
                          {t.recruit.btn}
                        </Button>
                      </Stack>
                    </motion.div>
                  </Grid.Col>

                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: false, amount: 0.3 }}
                      transition={{ duration: 0.8, delay: 0.2 }}
                    >
                      <Image
                        src={process.env.PUBLIC_URL + "/images/teens.png"}
                        alt="Young professionals"
                        w="100%"
                        style={{ zIndex: 1, filter: 'drop-shadow(0px 10px 20px rgba(0,0,0,0.1))' }}
                      />
                    </motion.div>
                  </Grid.Col>
                </Grid>
              </Container>
            </Box>

            {/* WIN-WIN SECTION (CHECKERBOARD) */}
            <Box id="win-win" py="md" bg="white">
              <Container size="lg">
                <Stack gap="md">
                  <motion.div
                    initial="hidden" whileInView="visible" viewport={{ once: false, amount: 0.5 }}
                    variants={fadeInUpVariants}
                  >
                    <Stack align="center" gap="md" ta="center">
                      <Badge size="lg" radius="md" tt="uppercase" style={{ border: '2px solid' }}>
                        RU'YA IMPACT
                      </Badge>
                      <Title order={2} c="dark.8" fz={{ base: 28, sm: 36, md: 48, lg: 54 }} lh={1.1} style={{ letterSpacing: '-1px' }}>
                        {t.winWin.title}
                      </Title>
                      <Text c="dimmed" size="md" maw={{ base: '100%', md: 700 }}>
                        {t.winWin.subtitle}
                      </Text>
                    </Stack>
                  </motion.div>

                  <Stack gap="md">
                    {/* ROW 1: Image Left, Text Right */}
                    <Grid gutter={{ base: 30, sm: 40, md: 60 }} align="center">
                      <Grid.Col span={{ base: 12, md: 6 }}>
                        <motion.div
                          initial={{ opacity: 0, x: -50 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: false, amount: 0.4 }}
                          transition={{ duration: 0.8, ease: "easeOut" }}
                        >
                          <Box style={{ position: 'relative' }}>
                            <Box bg="salmon.6" visibleFrom="sm" style={{ position: 'absolute', top: 20, left: -20, width: '100%', height: '100%', borderRadius: '24px', zIndex: 0 }} />
                            <Image
                              src={process.env.PUBLIC_URL + "/images/delivery-guy.jpg"}
                              alt="Active courier"
                              radius="xl"
                              h={{ base: 250, sm: 350, md: 450 }}
                              fit="cover"
                              style={{ position: 'relative', zIndex: 1, boxShadow: '0 20px 40px rgba(0,0,0,0.15)' }}
                            />
                          </Box>
                        </motion.div>
                      </Grid.Col>
                      <Grid.Col span={{ base: 12, md: 6 }}>
                        <motion.div
                          initial={{ opacity: 0, x: 30 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: false, amount: 0.4 }}
                          transition={{ duration: 0.8, delay: 0.2 }}
                        >
                          <Stack gap="md">
                            <ThemeIcon size={48} radius="xl" color="salmon.6" variant="light">
                              <IconTruckDelivery size={window.innerWidth < 640 ? 24 : 32} />
                            </ThemeIcon>
                            <Title order={3} fz={{ base: 24, sm: 28, md: 36, lg: 42 }} lh={1.1} c="dark.8">
                              {t.winWin.items[0].title}
                            </Title>
                            <Text size="lg" c="dimmed" lh={1.6}>
                              {t.winWin.items[0].desc}
                            </Text>
                            <Button
                              variant="filled" color="salmon.6" size="md" radius="xl"
                              rightSection={<IconArrowRight size={20} />}
                              onClick={() => smoothScroll('cta')}
                              style={{ alignSelf: 'flex-start', boxShadow: '0 10px 25px rgba(255, 117, 58, 0.3)', transition: 'all 0.3s ease' }}
                              styles={{ root: { '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 15px 35px rgba(255, 117, 58, 0.4)' } } }}
                            >
                              {t.recruit.btn}
                            </Button>
                          </Stack>
                        </motion.div>
                      </Grid.Col>
                    </Grid>

                    {/* ROW 2: Text Left, Image Right */}
                    <Grid gutter={{ base: 30, sm: 40, md: 60 }} align="center">
                      <Grid.Col span={{ base: 12, md: 6 }} order={{ base: 2, md: 1 }}>
                        <motion.div
                          initial={{ opacity: 0, x: -30 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: false, amount: 0.4 }}
                          transition={{ duration: 0.8, delay: 0.2 }}
                        >
                          <Stack gap="md">
                            <ThemeIcon size={48} radius="xl" color="#426457" variant="light">
                              <IconDeviceLaptop size={window.innerWidth < 640 ? 24 : 32} />
                            </ThemeIcon>
                            <Title order={3} fz={{ base: 24, sm: 28, md: 36, lg: 42 }} lh={1.1} c="dark.8">
                              {t.winWin.items[1].title}
                            </Title>
                            <Text size="lg" c="dimmed" lh={1.6}>
                              {t.winWin.items[1].desc}
                            </Text>
                            <Button
                              variant="filled" bg="#426457" c="white" size="md" radius="xl"
                              rightSection={<IconArrowRight size={20} />}
                              onClick={() => smoothScroll('cta')}
                              style={{ alignSelf: 'flex-start', boxShadow: '0 10px 25px rgba(66, 100, 87, 0.3)', transition: 'all 0.3s ease' }}
                              styles={{ root: { '&:hover': { backgroundColor: '#355046', transform: 'translateY(-3px)', boxShadow: '0 15px 35px rgba(66, 100, 87, 0.4)' } } }}
                            >
                              {t.recruit.btn}
                            </Button>
                          </Stack>
                        </motion.div>
                      </Grid.Col>

                      <Grid.Col span={{ base: 12, md: 6 }} order={{ base: 1, md: 2 }}>
                        <motion.div
                          initial={{ opacity: 0, x: 50 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: false, amount: 0.4 }}
                          transition={{ duration: 0.8, ease: "easeOut" }}
                        >
                          <Box style={{ position: 'relative' }}>
                            <Box bg="#426457" visibleFrom="sm" style={{ position: 'absolute', bottom: 20, right: -20, width: '100%', height: '100%', borderRadius: '24px', zIndex: 0 }} />
                            <Image
                              src={process.env.PUBLIC_URL + "/images/creative-girl.jpg"}
                              alt="Creative freelancer"
                              radius="xl"
                              h={{ base: 250, sm: 350, md: 450 }}
                              fit="cover"
                              style={{ position: 'relative', zIndex: 1, boxShadow: '0 20px 40px rgba(0,0,0,0.15)' }}
                            />
                          </Box>
                        </motion.div>
                      </Grid.Col>
                    </Grid>
                  </Stack>
                </Stack>
              </Container>
            </Box>

            {/* TESTIMONIALS */}
            <Box
              id="testimonials"
              ref={targetRef}
              bg="#426457"
              h={{ base: 'auto', md: '300vh' }}
              style={{ position: 'relative', zIndex: 1 }}
            >
              <HorizontalScroll targetRef={targetRef} testimonials={testimonials} t={t} />
            </Box>

            {/* CTA SECTION */}
            <Box id="cta" bg="lightYellow.5" pt="md" style={{ position: 'relative', zIndex: 0 }}>
              <Container size="lg">
                <motion.div
                  initial="hidden" whileInView="visible" viewport={{ once: false, amount: 0.3 }}
                  variants={staggerContainerVariants}
                >
                  <Stack align="center" gap="md">
                    <motion.div variants={fadeInUpVariants}>
                      <Title ta="center" order={1} c="grayTurquoise.8" fz={{ base: 32, sm: 48, md: 60, lg: 72 }} lh={1.1}>
                        {t.cta.title}
                      </Title>
                    </motion.div>

                    <motion.div variants={fadeInUpVariants}>
                      <Image
                        src={process.env.PUBLIC_URL + "/images/screenshot.png"}
                        alt="App Screenshot"
                        maw={{ base: '100%', sm: 600, md: 750, lg: 850 }} radius="xl" mt="md"
                        style={{ zIndex: 1 }}
                      />
                    </motion.div>

                    <motion.div variants={fadeInUpVariants}>
                      <Text ta="center" size="md" c="dimmed" maw={{ base: '100%', md: 600 }} mt="sm">
                        {t.cta.desc}
                      </Text>
                    </motion.div>

                    <motion.div variants={fadeInUpVariants}>
                      <Group mt="md" gap="md">
                        <Button
                          component="a" href="https://apps.apple.com/app/ruya/id6742221001"
                          size="md" radius="xl" variant="filled" color="salmon.6"
                          leftSection={<IconBrandApple size={window.innerWidth < 640 ? 20 : 24} />}
                          style={{ boxShadow: '0 10px 30px rgba(255, 117, 58, 0.35)', transition: 'all 0.3s ease', border: '1px solid rgba(255, 255, 255, 0.2)' }}
                          styles={{ root: { '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 20px 40px rgba(255, 117, 58, 0.5)' } } }}
                        >
                          App Store
                        </Button>
                        <Button
                          component="a" href="https://play.google.com/store/apps/details?id=com.mumina.ruya&hl=en"
                          size="md" radius="xl" variant="filled" color="salmon.6"
                          leftSection={<IconBrandGooglePlay size={window.innerWidth < 640 ? 20 : 24} />}
                          style={{ boxShadow: '0 10px 30px rgba(255, 117, 58, 0.35)', transition: 'all 0.3s ease', border: '1px solid rgba(255, 255, 255, 0.2)' }}
                          styles={{ root: { '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 20px 40px rgba(255, 117, 58, 0.5)' } } }}
                        >
                          Google Play
                        </Button>
                      </Group>
                    </motion.div>
                  </Stack>
                </motion.div>
              </Container>
            </Box>

            {/* FOOTER */}
            <Box id="footer" pt="md" style={{ position: 'relative', overflow: 'hidden' }}>
              {/* Mascot Behind Wall */}
              <motion.div
                viewport={{ once: true }}
                transition={{ type: 'spring', stiffness: 100, damping: 15, delay: 0.2 }}
                whileHover={{ y: '0%' }}
                style={{
                  position: 'absolute', bottom: window.innerWidth < 640 ? '166px' : '266px', left: '50%', x: '-50%',
                  zIndex: 0,
                }}
              >
                <Image
                  src={process.env.PUBLIC_URL + "/images/mascot-3.png?v=8"}
                  alt="Mascot"
                  w={{ base: 100, xs: 140, sm: 220, md: 270 }}
                  style={{ filter: 'drop-shadow(0px 10px 20px rgba(0,0,0,0.1))' }}
                />
              </motion.div>

              {/* Main Footer Content */}
              <Box
                bg="#426457"
                py="md"
                style={{ position: 'relative', zIndex: 1, borderTop: '1px solid rgba(255,255,255,0.1)' }}
              >
                <Container size="lg">
                  <Group justify="space-between" align="flex-end" gap="md">
                    <Stack gap={0}>
                      <Text
                        c="white" fw={900} lh={0.8}
                        style={{ fontSize: 'clamp(2.5rem, 8vw, 8rem)', letterSpacing: '-0.02em' }}
                      >
                        RU'YA
                      </Text>
                      <Text c="salmon.6" fw={700} size="md" mt="md">
                        {t.footer.slogan}
                      </Text>
                    </Stack>

                    <Stack align="flex-end" gap="md">
                      <Group gap="md">
                        <ActionIcon
                          component="a" href="https://instagram.com/ruya.space" target="_blank"
                          size="md" radius="xl" variant="white" color="dark"
                          style={{ boxShadow: '0 10px 20px rgba(0,0,0,0.1)', transition: 'transform 0.2s ease' }}
                        >
                          <IconBrandInstagram size={window.innerWidth < 640 ? 20 : 28} stroke={1.5} color="#426457" />
                        </ActionIcon>
                        <ActionIcon
                          component="a" href="https://t.me/+998777674466" target="_blank"
                          size="md" radius="xl" variant="white" color="dark"
                          style={{ boxShadow: '0 10px 20px rgba(0,0,0,0.1)', transition: 'transform 0.2s ease' }}
                        >
                          <IconBrandTelegram size={window.innerWidth < 640 ? 20 : 28} stroke={1.5} color="#426457" />
                        </ActionIcon>
                      </Group>

                      <Anchor
                        href="tel:+998777674466"
                        c="white" fw={700} fz={{ base: 'md', sm: 'lg', md: 'xl' }}
                        style={{ textDecoration: 'none', letterSpacing: '0.5px' }}
                      >
                        +998 77 767 44 66
                      </Anchor>

                      <Text c="rgba(255,255,255,0.4)" size="md" fw={500}>
                        © 2025 RU'YA Platform.
                      </Text>
                    </Stack>
                  </Group>
                </Container>
              </Box>
            </Box>
          </Box>
        </Stack>
      </AppShell.Main>
    </AppShell>
  );
};

export default LandingPage;